import React, { useState, useEffect } from 'react';
import { useDashboard } from '../store/useDashboard';
import useAuthStore from '../store/authStore';
import { demandeService, filialeService } from '../services/api';
import { LABELS_ROLES } from '../constants/roles';
import {
  IconeListe, IconeCrayon, IconeCompte, IconeValide, IconeAppareilPhoto, IconeImage,
  IconeLoupe, IconeRafraichir, IconeRefuse, IconeFeu, IconeEpingle, IconeAlerte,
  IconeTrombone, IconeDocument, IconeHorloge, IconeEclair, IconeBatiment,
  IconeInstitution, IconeUtilisateurs, IconeAmpoule, IconeFermer,
} from '../components/IconesSVG';

const STEPS = [
  { label: 'Type', icon: IconeListe, desc: 'Nature de la demande' },
  { label: 'Informations', icon: IconeCrayon, desc: 'Details du besoin' },
  { label: 'Validateur', icon: IconeCompte, desc: 'Circuit de validation' },
  { label: 'Confirmation', icon: IconeValide, desc: 'Recapitulatif' },
];

// ===== COMPOSANT SCANNER =====
function ScannerFiche({ onExtracted }) {
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState(null);
  const [resultat, setResultat] = useState(null);
  const [erreur, setErreur] = useState('');

  const extraireData = (texte) => {
    const lignes = texte.split('\n').map(l => l.trim()).filter(Boolean);
    const data = {};

    // Chercher le motif / objet
    const motifLine = lignes.find(l => /motif|objet|besoin|intitule/i.test(l));
    if (motifLine) {
      data.motif = motifLine.replace(/motif|objet|besoin|intitule/i, '').replace(':', '').trim();
    }
    // Si pas trouvé — prendre la première ligne substantielle
    if (!data.motif) {
      data.motif = lignes.find(l => l.length > 10) || '';
    }

    // Chercher le montant
    const montantLine = lignes.find(l => /montant|cout|prix|fcfa|total/i.test(l));
    if (montantLine) {
      const nums = montantLine.match(/[\d\s.,]+/g);
      if (nums) data.montant = nums[0].replace(/[\s,]/g, '').replace('.', '');
    }

    // Chercher le receptionnaire
    const recepLine = lignes.find(l => /receptionnaire|destinataire|livre|remis/i.test(l));
    if (recepLine) {
      data.receptionnaire = recepLine.replace(/receptionnaire|destinataire|livre|remis/i, '').replace(':', '').trim();
    }

    // Chercher l'adresse
    const adresseLine = lignes.find(l => /adresse|lieu|site|chantier/i.test(l));
    if (adresseLine) {
      data.adresse = adresseLine.replace(/adresse|lieu|site|chantier/i, '').replace(':', '').trim();
    }

    // Description generale
    data.description = lignes.slice(0, 8).join(' ').substring(0, 300);

    return data;
  };

  const analyser = async (file) => {
    setScanning(true);
    setProgress(0);
    setErreur('');
    setResultat(null);
    const url = URL.createObjectURL(file);
    setPreview(url);

    try {
      // Import dynamique de Tesseract
      const Tesseract = await import('tesseract.js');
      const result = await Tesseract.recognize(file, 'fra', {
        logger: m => {
          if (m.status === 'recognizing text') {
            setProgress(Math.round(m.progress * 100));
          }
        }
      });
      const texte = result.data.text;
      const data = extraireData(texte);
      setResultat({ texte, data });
      onExtracted(data);
    } catch (err) {
      console.error(err);
      setErreur('Erreur lors de la lecture — essayez une photo plus nette et mieux eclairee');
    } finally {
      setScanning(false);
    }
  };

  return (
    <div>
      {!preview ? (
        <div style={{ border: '2px dashed #E5E7EB', borderRadius: 14, padding: 40, textAlign: 'center', background: '#FAFAFA' }}>
          <div style={{ marginBottom: 14, color: '#ccc' }}><IconeAppareilPhoto taille={56} /></div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#0D1B2A', marginBottom: 8 }}>
            Scanner votre fiche EB
          </div>
          <div style={{ fontSize: 13, color: '#888', marginBottom: 6, lineHeight: 1.6 }}>
            Prenez une photo nette de votre fiche remplie.<br />
            Le système lira automatiquement les informations.
          </div>
          <div style={{ fontSize: 12, background: '#FEF9E7', border: '0.5px solid #F9E79F', borderRadius: 8, padding: '8px 14px', display: 'inline-block', marginBottom: 24, color: '#7D6608' }}>
            <IconeAmpoule taille={13} /> Conseil : bonne lumière · fiche bien cadrée · texte lisible
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
            <label style={{ display: 'inline-block', padding: '13px 28px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #2980B9, #1A5E8A)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', width: '100%', textAlign: 'center', boxSizing: 'border-box' }}>
              <IconeAppareilPhoto taille={15} /> Prendre une photo
              <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
                onChange={e => e.target.files[0] && analyser(e.target.files[0])} />
            </label>
            <label style={{ display: 'inline-block', padding: '11px 28px', borderRadius: 10, border: '1.5px solid #ddd', background: '#fff', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', width: '100%', textAlign: 'center', boxSizing: 'border-box', color: '#555' }}>
              <IconeImage taille={15} /> Choisir depuis la galerie
              <input type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => e.target.files[0] && analyser(e.target.files[0])} />
            </label>
          </div>
        </div>
      ) : (
        <div>
          <img src={preview} alt="scan"
            style={{ width: '100%', borderRadius: 12, marginBottom: 16, maxHeight: 280, objectFit: 'contain', background: '#111' }} />

          {scanning ? (
            <div style={{ background: '#EAF4FB', borderRadius: 10, padding: '16px 20px' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#1A5E8A', marginBottom: 10 }}>
                <IconeLoupe taille={14} /> Lecture de la fiche en cours... {progress}%
              </div>
              <div style={{ height: 8, background: '#ddd', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', background: 'linear-gradient(90deg, #2980B9, #1A5E8A)', width: progress + '%', borderRadius: 4, transition: 'width 0.3s' }} />
              </div>
              <div style={{ fontSize: 11, color: '#888', marginTop: 8 }}>
                Analyse du texte... Veuillez patienter
              </div>
            </div>
          ) : resultat ? (
            <div>
              <div style={{ background: '#EAFAF1', border: '1px solid #27AE60', borderRadius: 10, padding: '14px 16px', marginBottom: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1E8449', marginBottom: 6 }}>
                  <IconeValide taille={14} /> Lecture terminee !
                </div>
                <div style={{ fontSize: 13, color: '#555', marginBottom: 10 }}>
                  Les champs ont ete pre-remplis automatiquement. Verifiez et corrigez si necessaire.
                </div>
                {/* Apercu des donnees extraites */}
                {Object.entries(resultat.data).filter(([k, v]) => v && k !== 'description').map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', gap: 8, padding: '3px 0', fontSize: 12 }}>
                    <span style={{ color: '#888', minWidth: 100, textTransform: 'capitalize' }}>
                      {k === 'motif' ? 'Motif' : k === 'montant' ? 'Montant' : k === 'receptionnaire' ? 'Receptionnaire' : k === 'adresse' ? 'Adresse' : k}
                    </span>
                    <span style={{ color: '#0D1B2A', fontWeight: 500, flex: 1 }}>{String(v).substring(0, 60)}</span>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => { setPreview(null); setResultat(null); }}
                  style={{ flex: 1, padding: '10px', borderRadius: 8, border: '0.5px solid #ddd', background: '#fff', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
                  <IconeRafraichir taille={14} /> Rescanner
                </button>
                <div style={{ flex: 2, padding: '10px 14px', borderRadius: 8, background: '#F9FAFB', fontSize: 12, color: '#888', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  ↓ Remplissez les champs manquants ci-dessous
                </div>
              </div>
            </div>
          ) : erreur ? (
            <div>
              <div style={{ background: '#FDEDEC', border: '0.5px solid #C0392B', color: '#96281B', padding: '12px 16px', borderRadius: 10, fontSize: 13, marginBottom: 14, lineHeight: 1.5 }}>
                <IconeRefuse taille={14} /> {erreur}
              </div>
              <button onClick={() => { setPreview(null); setErreur(''); }}
                style={{ padding: '10px 20px', borderRadius: 8, border: '0.5px solid #ddd', background: '#fff', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
                <IconeRafraichir taille={14} /> Reessayer
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default function NouvelleDemandePage() {
  const { allerDashboard } = useDashboard();
  const { user } = useAuthStore();
  const [step, setStep] = useState(0);
  const [filiales, setFiliales] = useState([]);
  const [validateurs, setValidateurs] = useState([]);
  const [chargementValidateurs, setChargementValidateurs] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [scanTermine, setScanTermine] = useState(false);

  const [form, setForm] = useState({
    type: 'formulaire',
    isRegularisation: false,
    urgence: false,
    filiale: '',
    motif: '',
    expressionBesoin: '',
    dateLivraisonSouhaitee: '',
    adresseLivraison: '',
    receptionnaire: '',
    montantEstime: '',
    circuitChoisi: '',
    validateurId: '',
    dateAchatReel: '',
    approuvePar: '',
    moyenApprobation: 'telephone',
    documents: []
  });

  useEffect(() => {
    filialeService.getAll().then(res => {
      const data = res.data || [];
      setFiliales(data);
      if (data.length > 0) {
        const filialeUser = user?.roles?.[0]?.filiale?._id || data[0]._id;
        setForm(f => ({ ...f, filiale: filialeUser }));
        chargerValidateurs(filialeUser);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- effet volontairement limite au montage
  }, []);

  const chargerValidateurs = async (filialeId) => {
    if (!filialeId) return;
    setChargementValidateurs(true);
    setForm(f => ({ ...f, circuitChoisi: '', validateurId: '' }));
    try {
      const res = await filialeService.getValidateurs(filialeId);
      setValidateurs(res.data || []);
    } catch (err) {
      setValidateurs([]);
    } finally {
      setChargementValidateurs(false);
    }
  };

  const update = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleFiles = (e) => {
    const files = Array.from(e.target.files);
    setForm(f => ({ ...f, documents: [...f.documents, ...files] }));
  };

  const supprimerFichier = (index) => {
    setForm(f => ({ ...f, documents: f.documents.filter((_, i) => i !== index) }));
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' o';
    if (bytes < 1048576) return Math.round(bytes / 1024) + ' Ko';
    return (bytes / 1048576).toFixed(1) + ' Mo';
  };

  const getRoleValidateur = (v) => v.roles?.find(r => ['chef_projet', 'responsable_service'].includes(r.role))?.role || '';
  const getRoleLabel = (role) => LABELS_ROLES[role] || role;
  const getRoleIcon = (role) => role === 'chef_projet' ? <IconeInstitution taille={16} /> : role === 'responsable_service' ? <IconeCompte taille={16} /> : <IconeUtilisateurs taille={16} />;

  const pasDeValidateur = validateurs.length === 0 && !chargementValidateurs;

  const stepValide = () => {
    if (step === 0) return true;
    if (step === 1) {
      if (form.type === 'scan') return scanTermine && form.motif.trim() !== '';
      return form.motif.trim() !== '' && form.filiale !== '';
    }
    if (step === 2) return pasDeValidateur || form.circuitChoisi !== '';
    return true;
  };

  const getValidateurLabel = () => {
    if (pasDeValidateur) return 'Transmission directe au Preparateur Memo Filiale';
    const v = validateurs.find(v => v._id === form.validateurId);
    if (!v) return '—';
    return v.prenom + ' ' + v.nom + ' (' + getRoleLabel(getRoleValidateur(v)) + ')';
  };

  const soumettre = async () => {
    setSaving(true); setError('');
    try {
      const formData = new FormData();
      formData.append('type', 'expression_besoin');
      formData.append('filiale', form.filiale);
      formData.append('motif', form.motif);
      formData.append('expressionBesoin', form.expressionBesoin || '');
      formData.append('dateLivraisonSouhaitee', form.dateLivraisonSouhaitee || '');
      formData.append('adresseLivraison', form.adresseLivraison || '');
      formData.append('receptionnaire', form.receptionnaire || '');
      formData.append('montantEstime', form.montantEstime || '');
      formData.append('urgence', form.urgence);
      formData.append('isRegularisation', form.isRegularisation);
      if (form.circuitChoisi) formData.append('circuitChoisi', form.circuitChoisi);
      if (form.validateurId) formData.append('validateurId', form.validateurId);
      if (form.isRegularisation) {
        formData.append('dateAchatReel', form.dateAchatReel || '');
        formData.append('approuvePar', form.approuvePar || '');
        formData.append('moyenApprobation', form.moyenApprobation || 'telephone');
      }
      form.documents.forEach(f => formData.append('documents', f));
      await demandeService.create(formData);
      allerDashboard();
    } catch (err) {
      setError(err.message || 'Erreur lors de la creation');
    } finally {
      setSaving(false); }
  };

  const progression = ((step + 1) / STEPS.length) * 100;

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button onClick={() => allerDashboard()} style={s.backBtn}>← Retour</button>
        <div style={{ flex: 1 }}>
          <div style={s.pageTitle}>Nouvelle demande</div>
          <div style={s.pageSub}>Fiche d expression de besoin · Etape {step + 1} sur {STEPS.length}</div>
        </div>
        {form.urgence && <span style={s.urgentBadge}><IconeFeu taille={11} /> Urgent</span>}
        {form.isRegularisation && <span style={s.regulBadge}><IconeRafraichir taille={11} /> Regularisation</span>}
      </div>

      <div style={s.progressBar}>
        <div style={{ ...s.progressFill, width: progression + '%' }} />
      </div>

      <div style={s.content}>
        <div style={s.stepper}>
          {STEPS.map((st, i) => {
            const fait = i < step;
            const actif = i === step;
            return (
              <React.Fragment key={i}>
                <div style={s.stepItem}>
                  <div style={{ ...s.stepCircle, background: fait ? '#27AE60' : actif ? '#C0392B' : '#F5F6FA', border: fait ? '2px solid #27AE60' : actif ? '2px solid #C0392B' : '2px solid #E5E7EB', color: fait || actif ? '#fff' : '#aaa' }}>
                    {fait ? <IconeValide taille={16} /> : <st.icon taille={16} />}
                  </div>
                  <div style={s.stepText}>
                    <div style={{ ...s.stepLabel, color: actif ? '#C0392B' : fait ? '#27AE60' : '#aaa' }}>{st.label}</div>
                    <div style={s.stepDesc}>{st.desc}</div>
                  </div>
                </div>
                {i < STEPS.length - 1 && <div style={{ ...s.stepLine, background: i < step ? '#27AE60' : '#E5E7EB' }} />}
              </React.Fragment>
            );
          })}
        </div>

        <div style={s.card}>
          {/* ETAPE 0 */}
          {step === 0 && (
            <div>
              <div style={s.sectionTitle}>Comment souhaitez-vous creer votre demande ?</div>
              <div style={s.optionsGrid}>
                {[
                  { key: 'formulaire', icon: IconeCrayon, title: 'Formulaire digital', desc: 'Remplissez les champs directement sur l ecran. Recommande.' },
                  { key: 'scan', icon: IconeAppareilPhoto, title: 'Scanner une fiche', desc: 'Prenez en photo une fiche EB deja remplie. Les champs seront remplis automatiquement.' },
                ].map(opt => (
                  <div key={opt.key} onClick={() => update('type', opt.key)}
                    style={{ ...s.optionCard, ...(form.type === opt.key ? s.optionSelected : {}) }}>
                    <div style={s.optionIcon}><opt.icon taille={26} /></div>
                    <div style={s.optionTitle}>{opt.title}</div>
                    <div style={s.optionDesc}>{opt.desc}</div>
                    {form.type === opt.key && <div style={s.optionCheck}><IconeValide taille={13} /></div>}
                  </div>
                ))}
              </div>
              <div style={s.divider} />
              <div style={s.sectionTitle}>Type de demande</div>
              <div style={s.toggleGroup}>
                {[
                  { key: false, icon: IconeListe, label: 'Demande normale', color: '#2980B9', bg: '#EAF4FB' },
                  { key: true, icon: IconeRafraichir, label: 'Regularisation', color: '#935116', bg: '#FDF2E9' },
                ].map(opt => (
                  <div key={String(opt.key)} onClick={() => update('isRegularisation', opt.key)}
                    style={{ ...s.toggleCard, ...(form.isRegularisation === opt.key ? { border: `2px solid ${opt.color}`, background: opt.bg } : {}) }}>
                    <span style={{ display: 'inline-flex' }}><opt.icon taille={18} /></span>
                    <span style={{ fontSize: 13, fontWeight: form.isRegularisation === opt.key ? 600 : 400, color: form.isRegularisation === opt.key ? opt.color : '#555' }}>{opt.label}</span>
                  </div>
                ))}
              </div>
              {form.isRegularisation && (
                <div style={s.alertBox}><IconeAlerte taille={13} /> Une regularisation concerne un achat deja effectue apres approbation verbale. Le dossier doit etre complete dans les <strong>7 a 10 jours</strong>.</div>
              )}
              <div style={{ ...s.divider, marginTop: 16 }} />
              <div style={s.sectionTitle}>Niveau d urgence</div>
              <div style={s.toggleGroup}>
                {[
                  { key: false, icon: IconeEpingle, label: 'Normal', color: '#2980B9', bg: '#EAF4FB' },
                  { key: true, icon: IconeFeu, label: 'Urgent — traitement prioritaire', color: '#C0392B', bg: '#FDEDEC' },
                ].map(opt => (
                  <div key={String(opt.key)} onClick={() => update('urgence', opt.key)}
                    style={{ ...s.toggleCard, ...(form.urgence === opt.key ? { border: `2px solid ${opt.color}`, background: opt.bg } : {}) }}>
                    <span style={{ display: 'inline-flex' }}><opt.icon taille={18} /></span>
                    <span style={{ fontSize: 13, fontWeight: form.urgence === opt.key ? 600 : 400, color: form.urgence === opt.key ? opt.color : '#555' }}>{opt.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ETAPE 1 */}
          {step === 1 && (
            <div>
              <div style={s.sectionTitle}>Informations de la demande</div>

              {/* MODE SCAN */}
              {form.type === 'scan' && (
                <div style={{ marginBottom: 24 }}>
                  <ScannerFiche onExtracted={(data) => {
                    if (data.motif) update('motif', data.motif);
                    if (data.montant) update('montantEstime', data.montant);
                    if (data.receptionnaire) update('receptionnaire', data.receptionnaire);
                    if (data.adresse) update('adresseLivraison', data.adresse);
                    if (data.description) update('expressionBesoin', data.description);
                    setScanTermine(true);
                  }} />

                  {scanTermine && (
                    <div style={{ marginTop: 20, padding: '12px 16px', background: '#F9FAFB', borderRadius: 10, border: '0.5px solid #e5e7eb' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0D1B2A', marginBottom: 10 }}>
                        <IconeCrayon taille={13} /> Verifiez et completez les champs
                      </div>
                      <div style={s.formGrid}>
                        <div style={{ ...s.formGroup, gridColumn: '1 / -1' }}>
                          <label style={s.label}>Filiale <span style={{ color: '#C0392B' }}>*</span></label>
                          <select style={s.input} value={form.filiale} onChange={e => { update('filiale', e.target.value); chargerValidateurs(e.target.value); }}>
                            {filiales.map(f => <option key={f._id} value={f._id}>{f.code} — {f.nom}</option>)}
                          </select>
                        </div>
                        <div style={{ ...s.formGroup, gridColumn: '1 / -1' }}>
                          <label style={s.label}>Motif <span style={{ color: '#C0392B' }}>*</span></label>
                          <input style={{ ...s.input, border: form.motif ? '1.5px solid #27AE60' : '1.5px solid #E5E7EB' }}
                            value={form.motif} onChange={e => update('motif', e.target.value)} placeholder="Motif de la demande" />
                        </div>
                        <div style={s.formGroup}>
                          <label style={s.label}>Montant (FCFA)</label>
                          <input style={{ ...s.input, border: form.montantEstime ? '1.5px solid #27AE60' : '1.5px solid #E5E7EB' }}
                            type="number" value={form.montantEstime} onChange={e => update('montantEstime', e.target.value)} />
                        </div>
                        <div style={s.formGroup}>
                          <label style={s.label}>Receptionnaire</label>
                          <input style={{ ...s.input, border: form.receptionnaire ? '1.5px solid #27AE60' : '1.5px solid #E5E7EB' }}
                            value={form.receptionnaire} onChange={e => update('receptionnaire', e.target.value)} />
                        </div>
                        <div style={{ ...s.formGroup, gridColumn: '1 / -1' }}>
                          <label style={s.label}>Expression du besoin</label>
                          <textarea style={{ ...s.input, height: 80, resize: 'vertical' }}
                            value={form.expressionBesoin} onChange={e => update('expressionBesoin', e.target.value)} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* MODE FORMULAIRE */}
              {form.type !== 'scan' && (
                <div style={s.formGrid}>
                  <div style={{ ...s.formGroup, gridColumn: '1 / -1' }}>
                    <label style={s.label}>Filiale <span style={{ color: '#C0392B' }}>*</span></label>
                    <select style={s.input} value={form.filiale} onChange={e => { update('filiale', e.target.value); chargerValidateurs(e.target.value); }}>
                      {filiales.map(f => <option key={f._id} value={f._id}>{f.code} — {f.nom}</option>)}
                    </select>
                  </div>
                  <div style={{ ...s.formGroup, gridColumn: '1 / -1' }}>
                    <label style={s.label}>Motif de la demande <span style={{ color: '#C0392B' }}>*</span></label>
                    <input style={s.input} placeholder="Ex: Achat materiel chantier Nord"
                      value={form.motif} onChange={e => update('motif', e.target.value)} />
                    {form.motif.length > 0 && form.motif.length < 10 && (
                      <div style={{ fontSize: 11, color: '#E67E22', marginTop: 4 }}><IconeAlerte taille={11} /> Soyez plus precis (minimum 10 caracteres)</div>
                    )}
                  </div>
                  <div style={{ ...s.formGroup, gridColumn: '1 / -1' }}>
                    <label style={s.label}>Expression du besoin</label>
                    <textarea style={{ ...s.input, height: 100, resize: 'vertical' }}
                      placeholder="Decrivez en detail ce dont vous avez besoin..."
                      value={form.expressionBesoin} onChange={e => update('expressionBesoin', e.target.value)} />
                  </div>
                  <div style={s.formGroup}>
                    <label style={s.label}>Montant estime (FCFA)</label>
                    <input style={s.input} type="number" placeholder="0"
                      value={form.montantEstime} onChange={e => update('montantEstime', e.target.value)} />
                    {form.montantEstime && (
                      <div style={{ fontSize: 11, color: '#27AE60', marginTop: 4 }}>= {Number(form.montantEstime).toLocaleString('fr-FR')} FCFA</div>
                    )}
                  </div>
                  <div style={s.formGroup}>
                    <label style={s.label}>Date de livraison souhaitee</label>
                    <input style={s.input} type="date" value={form.dateLivraisonSouhaitee} onChange={e => update('dateLivraisonSouhaitee', e.target.value)} />
                  </div>
                  <div style={s.formGroup}>
                    <label style={s.label}>Adresse de livraison</label>
                    <input style={s.input} placeholder="Ex: Chantier Nord, Owendo" value={form.adresseLivraison} onChange={e => update('adresseLivraison', e.target.value)} />
                  </div>
                  <div style={s.formGroup}>
                    <label style={s.label}>Receptionnaire</label>
                    <input style={s.input} placeholder="Nom et prenom" value={form.receptionnaire} onChange={e => update('receptionnaire', e.target.value)} />
                  </div>
                  <div style={{ ...s.formGroup, gridColumn: '1 / -1' }}>
                    <label style={s.label}>Documents joints</label>
                    <label style={s.uploadZone}>
                      <div style={{ marginBottom: 6, color: '#ccc' }}><IconeTrombone taille={28} /></div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: '#0D1B2A' }}>Ajouter des fichiers</div>
                      <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>Devis, factures, photos, PDF...</div>
                      <input type="file" multiple accept="*/*" style={{ display: 'none' }} onChange={handleFiles} />
                    </label>
                    {form.documents.length > 0 && (
                      <div style={s.filesList}>
                        {form.documents.map((f, i) => (
                          <div key={i} style={s.fileItem}>
                            <span style={{ display: 'inline-flex' }}>{f.type?.includes('image') ? <IconeImage taille={16} /> : f.type?.includes('pdf') ? <IconeDocument taille={16} /> : <IconeTrombone taille={16} />}</span>
                            <span style={{ flex: 1, fontSize: 13 }}>{f.name}</span>
                            <span style={{ fontSize: 11, color: '#888' }}>{formatSize(f.size)}</span>
                            <button onClick={() => supprimerFichier(i)} style={s.fileDel}><IconeFermer taille={13} /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {form.isRegularisation && (
                    <>
                      <div style={{ gridColumn: '1 / -1', height: 1, background: '#E5E7EB' }} />
                      <div style={{ ...s.sectionTitle, gridColumn: '1 / -1' }}><IconeRafraichir taille={14} /> Informations de regularisation</div>
                      <div style={s.formGroup}>
                        <label style={s.label}>Date reelle de l achat <span style={{ color: '#C0392B' }}>*</span></label>
                        <input style={s.input} type="date" value={form.dateAchatReel} onChange={e => update('dateAchatReel', e.target.value)} />
                      </div>
                      <div style={s.formGroup}>
                        <label style={s.label}>Approuve verbalement par <span style={{ color: '#C0392B' }}>*</span></label>
                        <input style={s.input} placeholder="Nom de la personne" value={form.approuvePar} onChange={e => update('approuvePar', e.target.value)} />
                      </div>
                      <div style={s.formGroup}>
                        <label style={s.label}>Moyen d approbation</label>
                        <select style={s.input} value={form.moyenApprobation} onChange={e => update('moyenApprobation', e.target.value)}>
                          <option value="telephone">Telephone</option>
                          <option value="whatsapp">WhatsApp</option>
                          <option value="presentiel">En presentiel</option>
                        </select>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ETAPE 2 — VALIDATEUR */}
          {step === 2 && (
            <div>
              <div style={s.sectionTitle}>Choisissez votre validateur</div>
              <div style={{ fontSize: 13, color: '#888', marginBottom: 20, lineHeight: 1.5 }}>
                Ce validateur approuvera votre demande avant qu elle remonte a la Direction des Achats.
              </div>
              {chargementValidateurs ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>
                  <div style={{ marginBottom: 8, color: '#bbb' }}><IconeHorloge taille={28} /></div>Chargement...
                </div>
              ) : pasDeValidateur ? (
                <div style={{ ...s.alertBox, background: '#EAFAF1', border: '1px solid #27AE60', color: '#1E8449' }}>
                  <IconeValide taille={13} /> Aucun validateur disponible. Votre demande sera transmise <strong>directement au Preparateur Memo Filiale</strong>.
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: validateurs.length === 1 ? '1fr' : '1fr 1fr', gap: 12 }}>
                  {validateurs.map(v => {
                    const role = getRoleValidateur(v);
                    const estMoi = v._id === user?._id;
                    const selectionne = form.validateurId === v._id;
                    return (
                      <div key={v._id} onClick={() => { update('validateurId', v._id); update('circuitChoisi', role); }}
                        style={{ ...s.validateurCard, ...(selectionne ? s.validateurSelected : {}), ...(estMoi ? { border: '2px solid #E67E22', background: '#FDF2E9' } : {}) }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ width: 44, height: 44, borderRadius: '50%', background: selectionne ? '#27AE60' : estMoi ? '#E67E22' : '#0D1B2A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                            {getRoleIcon(role)}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#0D1B2A' }}>{v.prenom} {v.nom}</div>
                            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{getRoleLabel(role)}</div>
                            {estMoi && <div style={{ fontSize: 11, color: '#E67E22', fontWeight: 600, marginTop: 3 }}><IconeEclair taille={11} /> Auto-validation</div>}
                          </div>
                          {selectionne && (
                            <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#27AE60', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13, fontWeight: 700 }}><IconeValide taille={13} /></div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ETAPE 3 — CONFIRMATION */}
          {step === 3 && (
            <div>
              <div style={s.sectionTitle}>Recapitulatif de votre demande</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
                <span style={{ ...s.badge, background: '#EAF4FB', color: '#1A5E8A' }}><IconeListe taille={11} /> {form.isRegularisation ? 'Regularisation' : 'Demande normale'}</span>
                {form.urgence && <span style={{ ...s.badge, background: '#FDEDEC', color: '#96281B' }}><IconeFeu taille={11} /> Urgent</span>}
                {form.type === 'scan' && <span style={{ ...s.badge, background: '#F5EEF8', color: '#8E44AD' }}><IconeAppareilPhoto taille={11} /> Scanne</span>}
                <span style={{ ...s.badge, background: '#EAFAF1', color: '#1E8449' }}><IconeBatiment taille={11} /> {filiales.find(f => f._id === form.filiale)?.code || '—'}</span>
              </div>
              <div style={s.recapGrid}>
                {[
                  { label: 'Filiale', value: filiales.find(f => f._id === form.filiale)?.nom },
                  { label: 'Motif', value: form.motif, full: true },
                  { label: 'Montant estime', value: form.montantEstime ? Number(form.montantEstime).toLocaleString('fr-FR') + ' FCFA' : '—', red: true },
                  { label: 'Livraison souhaitee', value: form.dateLivraisonSouhaitee || '—' },
                  { label: 'Adresse', value: form.adresseLivraison || '—' },
                  { label: 'Receptionnaire', value: form.receptionnaire || '—' },
                  { label: 'Validateur', value: getValidateurLabel(), full: true },
                  { label: 'Documents joints', value: form.documents.length + ' fichier(s)' },
                  ...(form.isRegularisation ? [
                    { label: 'Date achat reel', value: form.dateAchatReel || '—' },
                    { label: 'Approuve par', value: (form.approuvePar || '—') + ' (' + form.moyenApprobation + ')' },
                  ] : [])
                ].map((item, i) => (
                  <div key={i} style={{ ...s.recapItem, ...(item.full ? { gridColumn: '1 / -1' } : {}) }}>
                    <div style={s.recapLabel}>{item.label}</div>
                    <div style={{ ...s.recapValue, ...(item.red ? { color: '#C0392B', fontSize: 16, fontWeight: 700 } : {}) }}>{item.value || '—'}</div>
                  </div>
                ))}
              </div>
              {form.expressionBesoin && (
                <div style={{ background: '#F9FAFB', borderRadius: 8, padding: 14, marginTop: 12, marginBottom: 12 }}>
                  <div style={s.recapLabel}>Expression du besoin</div>
                  <div style={{ fontSize: 13, color: '#333', lineHeight: 1.6, marginTop: 4 }}>{form.expressionBesoin}</div>
                </div>
              )}
              <div style={{ ...s.alertBox, background: '#EAF4FB', border: '1px solid #85C1E9', color: '#1A5E8A', marginTop: 0 }}>
                <IconeListe taille={13} /> Votre demande sera transmise a <strong>{getValidateurLabel()}</strong> puis suivra le circuit complet jusqu a la signature du PDG.
              </div>
              {error && <div style={s.errorMsg}>{error}</div>}
            </div>
          )}
        </div>

        <div style={s.navRow}>
          <button onClick={() => allerDashboard()} style={s.btnCancel}>Annuler</button>
          <div style={{ flex: 1 }} />
          {step > 0 && <button onClick={() => setStep(step - 1)} style={s.btnPrev}>← Precedent</button>}
          {step < STEPS.length - 1 ? (
            <button onClick={() => stepValide() && setStep(step + 1)}
              style={{ ...s.btnNext, opacity: stepValide() ? 1 : 0.5 }} disabled={!stepValide()}>
              Suivant →
            </button>
          ) : (
            <button onClick={soumettre} style={s.btnSoumettre} disabled={saving}>
              {saving ? <><IconeHorloge taille={14} /> Envoi en cours...</> : <><IconeValide taille={14} /> Soumettre la demande</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#F5F6FA', fontFamily: 'Inter, system-ui, sans-serif' },
  header: { background: '#fff', borderBottom: '0.5px solid #e5e7eb', padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 16 },
  backBtn: { background: 'transparent', border: '0.5px solid #ddd', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', flexShrink: 0 },
  pageTitle: { fontSize: 18, fontWeight: 600, color: '#0D1B2A' },
  pageSub: { fontSize: 12, color: '#888', marginTop: 2 },
  urgentBadge: { background: '#FDEDEC', color: '#96281B', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
  regulBadge: { background: '#FDF2E9', color: '#935116', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
  progressBar: { height: 3, background: '#F0F0F0' },
  progressFill: { height: '100%', background: 'linear-gradient(90deg, #C0392B, #E74C3C)', transition: 'width 0.3s ease' },
  content: { maxWidth: 720, margin: '0 auto', padding: '28px 20px' },
  stepper: { display: 'flex', alignItems: 'flex-start', marginBottom: 24 },
  stepItem: { display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 },
  stepCircle: { width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 },
  stepText: { display: 'flex', flexDirection: 'column' },
  stepLabel: { fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' },
  stepDesc: { fontSize: 10, color: '#aaa', marginTop: 1, whiteSpace: 'nowrap' },
  stepLine: { flex: 1, height: 2, margin: '0 8px', marginBottom: 16, minWidth: 20 },
  card: { background: '#fff', borderRadius: 14, border: '0.5px solid #e5e7eb', padding: 28, marginBottom: 16 },
  sectionTitle: { fontSize: 15, fontWeight: 600, color: '#0D1B2A', marginBottom: 16 },
  divider: { height: 1, background: '#F0F0F0', margin: '20px 0' },
  optionsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 },
  optionCard: { border: '2px solid #E5E7EB', borderRadius: 12, padding: 20, cursor: 'pointer', textAlign: 'center', position: 'relative', background: '#FAFAFA' },
  optionSelected: { border: '2px solid #C0392B', background: '#FDEDEC' },
  optionIcon: { fontSize: 36, marginBottom: 10 },
  optionTitle: { fontSize: 14, fontWeight: 600, color: '#0D1B2A', marginBottom: 4 },
  optionDesc: { fontSize: 12, color: '#888', lineHeight: 1.4 },
  optionCheck: { position: 'absolute', top: 10, right: 12, color: '#C0392B', fontWeight: 700, fontSize: 16 },
  toggleGroup: { display: 'flex', gap: 10 },
  toggleCard: { flex: 1, display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 10, border: '2px solid #E5E7EB', background: '#FAFAFA', cursor: 'pointer' },
  alertBox: { background: '#FEF9E7', border: '1px solid #F9E79F', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#7D6608', lineHeight: 1.5, marginTop: 12 },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  formGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 12, fontWeight: 600, color: '#555' },
  input: { padding: '10px 12px', borderRadius: 10, border: '1.5px solid #E5E7EB', fontSize: 13, fontFamily: 'inherit', outline: 'none', background: '#FAFAFA', boxSizing: 'border-box', width: '100%', color: '#0D1B2A' },
  uploadZone: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px', border: '2px dashed #E5E7EB', borderRadius: 10, cursor: 'pointer', background: '#FAFAFA', textAlign: 'center' },
  filesList: { marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 },
  fileItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#F0F9FF', borderRadius: 8, border: '0.5px solid #BAE0FF' },
  fileDel: { background: 'transparent', border: 'none', color: '#C0392B', cursor: 'pointer', fontSize: 14, padding: '0 4px', fontFamily: 'inherit' },
  validateurCard: { border: '2px solid #E5E7EB', borderRadius: 12, padding: '16px', cursor: 'pointer', background: '#FAFAFA' },
  validateurSelected: { border: '2px solid #27AE60', background: '#F0FFF4' },
  badge: { padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
  recapGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 },
  recapItem: { background: '#F9FAFB', borderRadius: 10, padding: '10px 14px' },
  recapLabel: { fontSize: 10, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 },
  recapValue: { fontSize: 13, color: '#0D1B2A', fontWeight: 500 },
  errorMsg: { background: '#FDEDEC', color: '#96281B', padding: '10px 14px', borderRadius: 10, fontSize: 13, marginTop: 12 },
  navRow: { display: 'flex', alignItems: 'center', gap: 10 },
  btnCancel: { padding: '10px 16px', borderRadius: 8, border: '0.5px solid #ddd', background: 'transparent', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', color: '#888' },
  btnPrev: { padding: '11px 20px', borderRadius: 10, border: '1.5px solid #ddd', background: '#fff', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 },
  btnNext: { padding: '11px 28px', borderRadius: 10, border: 'none', background: '#2980B9', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  btnSoumettre: { padding: '12px 32px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #27AE60, #1E8449)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 15px rgba(39,174,96,0.3)' },
  scanZone: { border: '2px dashed #ddd', borderRadius: 12, padding: 40, textAlign: 'center' },
  btnUpload: { display: 'inline-block', padding: '10px 20px', borderRadius: 8, border: '0.5px solid #ddd', background: '#fff', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' },
};
